import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import fetchData from './Util';

const configs = {
  ddevLocal: {
    serverless: 'https://europe-west3-circuit-bid-184512.cloudfunctions.net/serverless-eu-test/',
    // serverless: 'http://localhost:5000/',
  },
  devPantheon: {
    serverless: 'https://europe-west3-circuit-bid-184512.cloudfunctions.net/serverless-eu-test/',
  },
  testPantheon: {
    serverless: 'https://europe-west3-circuit-bid-184512.cloudfunctions.net/serverless-eu-test/',
  },
  livePantheon: {
    serverless: 'https://europe-west3-circuit-bid-184512.cloudfunctions.net/serverless-eu/',
  },
  livePantheonUS: {
    serverless: 'https://europe-west3-circuit-bid-184512.cloudfunctions.net/serverless-eu-test/',
  },
};

// Hardcoded config for jbull.com to bypass China firewall issues
const jbullConfig = {
  backendUrl: "https://live-bids.jbull.com/",
  name: "livePantheon",
  pusherKey: {
    key: "67608a61bc1954f76bf1",
    cluster: "EuWest1"
  },
  debug: false,
  serverless: "https://europe-west3-circuit-bid-184512.cloudfunctions.net/serverless-eu/",
  recaptchaKey: "6LdWt6YUAAAAAMdwSJ7vwnvcOtlRWx0nP19iS03Y",
  siteName: "backoffice-jbull"
};

const localDev = ['localhost'];

const useMockData = 0;

function getServerlessURL() {
  const hostname = window.location.hostname;

  if (localDev.includes(hostname) || hostname.match(/ddev\.site/)) {
    return configs.ddevLocal.serverless;
  } else if (hostname.match(/dev-[a-z-]+\.pantheonsite\.io/)) {
    return configs.devPantheon.serverless;
  } else if (hostname.match(/test-[a-z-]+\.pantheonsite\.io/)) {
    return configs.testPantheon.serverless;
  }

  // Default to live serverless.
  return configs.livePantheon.serverless;
}

const getConfigByHostname = (() => {
  let config = null;

  return async () => {
    if (config) {
      return config;
    }

    const hostname = window.location.hostname;

    // Check if this is jbull.com - return hardcoded config to bypass China firewall
    if (hostname === 'jbull.com' || hostname === 'www.jbull.com') {
      config = {
        ...jbullConfig,
        success: true,
        useMockData: useMockData,
      };
      return config;
    }

    // For affiliates we want to pass the site name.
    const siteElement = document.getElementById('circuit-setup');
    let siteName;
    let hostName;
    if (siteElement) {
      siteName = siteElement.getAttribute('site');
      hostName = siteElement.getAttribute('hostname') || window.location.hostname;
    }
    else {
      const siteElement_legacy = document.getElementById('site-name');
      if (siteElement_legacy) {
        siteName = siteElement_legacy.getAttribute('site');
      }
      else {
        // Display error message if no site name is found.
        console.error('Circuit config - No site name found in the DOM.');
      }
      hostName = window.location.hostname;
    }

    const serverlessURL = getServerlessURL();
    try {
      const response = await fetch(serverlessURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event: 'get_config',
          hostname: hostName,
          sitename: siteName || '',
        }),
      });

      if (response.ok) {
        config = await response.json();
      } else {
        console.error('Failed to fetch configuration:', response.statusText);
        return {
          ...configs.ddevLocal,
          success: false,
          useMockData: useMockData,
        };
      }
    } catch (error) {
      console.error('Error fetching configuration:', error);
      return {
        ...configs.ddevLocal,
        success: false,
        useMockData: useMockData,
      };
    }

    return {
      ...config,
      success: true,
      useMockData: useMockData,
    };
  };
})();

export const ConfigContext = createContext(null);

export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState({
    siteName: null,
    backendUrl: null,
    success: false,
    useMockData: 0,
    accessToken: null,
    siteData: {},
    userData: {},
    fetchUserData: null,
    setAccessToken: null,
  });
  const [loading, setLoading] = useState(true);

  const fetchSiteData = useCallback(async () => {
    const configData = await getConfigByHostname();
    try {
      const response = await fetchData(
        `${configData.backendUrl}/api/sites?name=${configData.siteName}`,
        { mockData: 'api-sites' }
      );

      if (response.ok) {
        console.log('Site data response:', response);
        const result = await response.json();
        const siteData = result.data[0];
        setConfig((prevConfig) => ({
          ...prevConfig,
          siteData,
        }));
      } else {
        console.log('Site data response:', response);
        throw new Error('Failed to fetch site data');
      }
    } catch (error) {
      console.error('Error fetching site data:', error);
    }
  }, []);

  const setAccessToken = useCallback((token) => {
    if (!token) {
      localStorage.removeItem('bs_access_token');
    } else {
      localStorage.setItem('bs_access_token', token);
    }
    setConfig((prevConfig) => ({
      ...prevConfig,
      accessToken: token,
    }));
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const configData = await getConfigByHostname();
      console.log('Config data:', configData);

      setConfig((prevConfig) => ({
        ...configData,
        userData: prevConfig?.userData || null,
        siteData: prevConfig?.siteData || null,
        accessToken: prevConfig?.accessToken,
        fetchUserData: fetchUserData,
        setAccessToken: setAccessToken,
      }));

      await fetchSiteData();
    } catch (error) {
      console.error('Error fetching configuration:', error);
    }
  }, [fetchSiteData, setAccessToken]);

  const fetchUserData = useCallback(async () => {
    try {
      if (config.accessToken && config.backendUrl) {
        // Check if force_bo_refresh flag is set in localStorage
        const forceRefresh = localStorage.getItem('force_bo_refresh');
        let url = `${config.backendUrl}/api/me?access_token=${config.accessToken}`;

        // Append force_bo_refresh parameter if it exists in localStorage
        if (forceRefresh === '1') {
          url += '&force_bo_refresh=1';
          // Clear the flag after using it
          localStorage.removeItem('force_bo_refresh');
        }

        const response = await fetchData(
            url,
            { mockData: 'api-me' }
        );

        if (response.ok) {
          const userData = await response.json();
          // If no bo token, then take from local storage.
          if (!userData.bo_access_token) {
            userData.bo_access_token = localStorage.getItem('bo_access_token') || null;
          }
          setConfig((prevConfig) => ({
            ...prevConfig,
            userData: userData,
          }));
        } else {
          console.log('User data response:', response);
          throw new Error('Failed to fetch user data');
        }
      } else {
        console.warn('Access token is empty. Skipping user data fetch.');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  }, [config.accessToken, config.backendUrl]);

  useEffect(() => {
    const fetchConfigAsync = async () => {
      const accessToken = localStorage.getItem('bs_access_token');
      setAccessToken(accessToken);
      console.log('Fetching config...');
      await fetchConfig();
      setLoading(false);
    };

    fetchConfigAsync();
  }, [fetchConfig, setAccessToken]);

  useEffect(() => {
    if (config.accessToken && config.backendUrl) {
      fetchUserData();
    }
  }, [config.accessToken, config.backendUrl, fetchUserData]);

  const loadCSS = useCallback((url) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (config.siteData && config.siteData.backoffice_url) {
      const cssUrl = `${config.siteData.backoffice_url}/css/user-ui.css`;
      // const cssUrl = `user-ui.css`;
      loadCSS(cssUrl);
    }
  }, [config.siteData, loadCSS]);

  const memoizedConfig = useMemo(() => config, [config]);

  if (loading) {
    return <div>Loading configuration...</div>;
  }

  return (
    <ConfigContext.Provider value={memoizedConfig}>
      {children}
    </ConfigContext.Provider>
  );
};

export function formatCurrencyAmount(amount, currency) {
  if (amount === null || amount === undefined) {
    return '';
  }
  
  // Convert amount to number
  const numAmount = Number(amount);
  
  let formatted;
  const numberFormat = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: true
  };

  switch (currency) {
    case 'CHF':
      formatted = `${numAmount.toLocaleString('de-CH', numberFormat)} CHF`;
      break;
    case 'EUR':
      formatted = `${numAmount.toLocaleString('de-DE', numberFormat)} €`;
      break;
    case 'HKD':
      formatted = `${numAmount.toLocaleString('en-US', numberFormat)} HK$`;
      break;
    case 'GBP':
      formatted = `${numAmount.toLocaleString('en-US', numberFormat)} £`;
      break;
    case 'ILS':
      formatted = `${numAmount.toLocaleString('en-US', numberFormat)} ₪`;
      break;
    case 'USD':
      formatted = `$${numAmount.toLocaleString('en-US', numberFormat)}`;
      break;
    default:
      formatted = numAmount.toLocaleString('en-US', numberFormat);
  }
  return formatted;
}